import { CardWidget, WIDGET_TYPE } from '../../../../types';
import { useTranslate } from '@bynder/localization';
import { Inspector, Thumbnail, token } from '@bynder/design-system';
import { useActiveWidget } from '../../../../queries/guides';
import { isDevelopment } from '../../../../constants/app';
import CustomCssStyleEditor from '../CustomCSSEditor';
import CardImageSelector from './CardWidgetSettings/CardImageSelector';
import { CardTextEditor } from './CardWidgetSettings/CardTextEditor';
import { CardLinkEditor } from './CardWidgetSettings/CardLinkEditor';
import { CardsInWidgetSettings } from './CardWidgetSettings/CardsInWidgetSettings';
import { SettingsAccordion } from '../SettingsAccordion';
import {
  useDefaultThemeset,
  useGetThemesetColors,
} from '../../../../queries/themesets';
import InspectorPanelColorPicker from '../ColorPicker';
import { defaultTransparent } from '../../../widgets/RichText/Config';
import { CSS_CLASSES, CSS_PROPERTIES } from '../constants';
import { useCardWidgetMutations } from '../../../../mutations/widgets/card';
import { useCustomCSSEditor } from '../../../../hooks/useCustomCSSEditor';
import { IconColor, IconColorStyle } from '@bynder/icons';
import { WidgetSettingsHeader } from '../../Styles';

const CardWidgetSettings = () => {
  const { translate } = useTranslate();
  const activeWidget = useActiveWidget() as CardWidget;
  const { editWidgetStyle } = useCardWidgetMutations();
  const handleStyleChange = (newStyle: string) => {
    editWidgetStyle.mutate({ newStyle });
  };

  return (
    <>
      <WidgetSettingsHeader
        widgetType={WIDGET_TYPE.CARD}
        subtext={translate('WIDGET_INSPECTOR.CARD_IMAGE_SUBTEXT')}
      />
      <CardImageSelector />
      <Inspector.Divider />
      <CardTextEditor />

      <CardColorsEditor
        style={activeWidget.style.stylesheet}
        handleStyleChange={handleStyleChange}
      />

      <CardLinkEditor />
      <CardsInWidgetSettings />
      {isDevelopment && (
        <CustomCssStyleEditor style={activeWidget.style.stylesheet} />
      )}
    </>
  );
};

export default CardWidgetSettings;

const CardColorsEditor = ({ style, handleStyleChange }) => {
  const { translate } = useTranslate();
  const themeset = useDefaultThemeset();
  const { findColorFromStyle, clearStyle, addColorToStyle } =
    useCustomCSSEditor(style);
  const customColors = useGetThemesetColors();

  const handleClear = (property, selector?) => {
    const newStyle = clearStyle(property, selector);
    handleStyleChange(newStyle);
  };

  const handleClick = (colorId, property, selector?) => {
    const newStyle = addColorToStyle(colorId, property, selector);
    handleStyleChange(newStyle);
  };

  const defaultCardTextColor =
    themeset?.widgets.card.titleColor?.hexCode || token.colorText;
  const defaultCardDescriptionColor =
    themeset?.widgets.card.descriptionColor?.hexCode || token.colorTextMuted;
  const defaultCardBorderColor =
    themeset?.widgets.card.borderColor?.hexCode || defaultTransparent.hexCode;

  return (
    <>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconColor />} />}
        title={translate('WIDGET_INSPECTOR.BUTTON_COLOR_BASE')}
      >
        <InspectorPanelColorPicker
          handleClick={colorId => {
            handleClick(colorId, CSS_PROPERTIES.COLOR, CSS_CLASSES.CARD_TITLE);
          }}
          handleClear={() => {
            handleClear(CSS_PROPERTIES.COLOR, CSS_CLASSES.CARD_TITLE);
          }}
          colors={customColors}
          activeColor={findColorFromStyle(
            CSS_PROPERTIES.COLOR,
            CSS_CLASSES.CARD_TITLE,
          )}
          defaultColorHex={defaultCardTextColor}
          label={translate('WIDGET_INSPECTOR.CARD_COLOR_TITLE')}
        />
        <InspectorPanelColorPicker
          handleClick={colorId => {
            handleClick(
              colorId,
              CSS_PROPERTIES.COLOR,
              CSS_CLASSES.CARD_DESCRIPTION,
            );
          }}
          activeColor={findColorFromStyle(
            CSS_PROPERTIES.COLOR,
            CSS_CLASSES.CARD_DESCRIPTION,
          )}
          handleClear={() => {
            handleClear(CSS_PROPERTIES.COLOR, CSS_CLASSES.CARD_DESCRIPTION);
          }}
          colors={customColors}
          defaultColorHex={defaultCardDescriptionColor}
          label={translate('WIDGET_INSPECTOR.CARD_COLOR_DESCRIPTION')}
        />
      </SettingsAccordion>
      <SettingsAccordion
        thumbnail={<Thumbnail icon={<IconColorStyle />} />}
        title={translate('WIDGET_INSPECTOR.BUTTON_COLOR_HOVER')}
      >
        <InspectorPanelColorPicker
          handleClick={colorId => {
            handleClick(colorId, CSS_PROPERTIES.BORDER_COLOR);
          }}
          activeColor={findColorFromStyle(CSS_PROPERTIES.BORDER_COLOR)}
          handleClear={() => {
            handleClear(CSS_PROPERTIES.BORDER_COLOR);
          }}
          colors={customColors}
          defaultColorHex={defaultCardBorderColor}
          label={translate('WIDGET_INSPECTOR.CARD_COLOR_BORDER')}
        />
      </SettingsAccordion>
    </>
  );
};
